/**
 * UTM forwarding for outbound campaign links (e.g. lu.ma event pages).
 *
 * The site is SSR-prerendered, so CTA hrefs are frozen at build time and
 * never see the visitor's live UTM params. These helpers let a link forward
 * the standard `utm_*` params from the current page URL onto its destination
 * at runtime, so attribution survives the click-through to an external host.
 *
 * lu.ma's native Insights reporting buckets on `utm_source`; the remaining
 * params are preserved for any downstream sync (e.g. lu.ma -> HubSpot).
 */

/** The five standard Urchin Tracking Module params, in canonical order. */
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

/** Click-identifier params worth persisting/forwarding alongside UTMs. */
const CLICK_ID_KEYS = ["gclid"] as const;

/** Every param we capture, persist, and forward. */
const TRACKED_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS] as const;

/** sessionStorage key holding the last-touch tracking params for the session. */
const STORAGE_KEY = "c1:utm";

function readStore(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    // sessionStorage unavailable (private mode / disabled) or corrupt JSON.
    return {};
  }
}

function writeStore(params: Record<string, string>): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    // sessionStorage unavailable — persistence is best-effort, so no-op.
  }
}

/**
 * Capture tracking params from `search` (e.g. `window.location.search`) into
 * sessionStorage, using a **last-touch** model: if this URL carries ANY tracked
 * param, the stored set is replaced wholesale with the params on this URL, so
 * the most recent campaign link wins and one campaign's params never mix with
 * another's. A URL with no tracking params leaves the stored set untouched, so
 * ordinary internal navigation never clears a visitor's attribution.
 *
 * Safe to call on every navigation; no-op during SSR and when storage is
 * unavailable.
 */
export function captureUtmParams(search: string): void {
  const incoming = new URLSearchParams(search);
  const captured: Record<string, string> = {};
  for (const key of TRACKED_KEYS) {
    const value = incoming.get(key);
    if (value) captured[key] = value;
  }
  if (Object.keys(captured).length > 0) {
    writeStore(captured);
  }
}

/**
 * The tracking params to forward onto outbound links / form submissions:
 * the persisted last-touch set, with any params on the live URL layered on top
 * (live URL wins) so forwarding still works before {@link captureUtmParams} has
 * run for the current tick.
 */
export function getForwardedParams(): Record<string, string> {
  const merged = readStore();
  if (typeof window !== "undefined") {
    const live = new URLSearchParams(window.location.search);
    for (const key of TRACKED_KEYS) {
      const value = live.get(key);
      if (value) merged[key] = value;
    }
  }
  return merged;
}

/** {@link getForwardedParams} rendered as a query-string fragment (no leading `?`). */
export function getForwardedSearch(): string {
  return new URLSearchParams(getForwardedParams()).toString();
}

/**
 * The current page URL with the persisted tracking params merged into its query
 * string (params already on the URL win). Used as the HubSpot `pageUri` so a
 * submission is attributed to the originating campaign even after the visitor
 * has navigated to a UTM-less URL. Returns `""` during SSR.
 */
export function currentHrefWithForwardedUtm(): string {
  if (typeof window === "undefined") return "";
  return withForwardedUtm(window.location.href, getForwardedSearch());
}

/** True if `url` points at a lu.ma event host (luma.com / lu.ma + subdomains). */
export function isLumaUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return (
      hostname === "luma.com" ||
      hostname === "lu.ma" ||
      hostname.endsWith(".luma.com") ||
      hostname.endsWith(".lu.ma")
    );
  } catch {
    return false;
  }
}

/**
 * Merge the tracked params (`utm_*` plus `gclid`) from `search` (e.g.
 * `window.location.search`) into `url`. Params already present on `url` win — an
 * explicit utm_source baked into a destination link is never clobbered. Returns
 * `url` unchanged when there is nothing to forward or `url` is not a valid
 * absolute URL.
 */
export function withForwardedUtm(url: string, search: string): string {
  if (!search) return url;

  let target: URL;
  try {
    target = new URL(url);
  } catch {
    return url;
  }

  const incoming = new URLSearchParams(search);
  let changed = false;
  for (const key of TRACKED_KEYS) {
    const value = incoming.get(key);
    if (value && !target.searchParams.has(key)) {
      target.searchParams.set(key, value);
      changed = true;
    }
  }

  return changed ? target.toString() : url;
}
